// src/lib/bookingTime.js
//
// Date/time helpers for the booking pages. Everything takes a timezone
// explicitly: a visitor's "today", a staff member's "today" and the
// business's "today" can be three different dates at the same instant, so
// nothing here reads the browser's zone implicitly.

const two = n => String(n).padStart(2, "0");

export const browserTimeZone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Toronto"; } catch { return "America/Toronto"; }
};

// "2026-01-14" for the calendar day an instant falls on in `tz`.
export function dateKey(ms, tz) {
  const p = {};
  for (const { type, value } of new Intl.DateTimeFormat("en-US", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(ms))) p[type] = value;
  return `${p.year}-${p.month}-${p.day}`;
}

export const fmtTime = (ms, tz) =>
  new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit" }).format(new Date(ms));

export const fmtLongDate = (ms, tz) =>
  new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "long", month: "long", day: "numeric", year: "numeric" }).format(new Date(ms));

export const fmtShortDate = (ms, tz) =>
  new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "short", month: "short", day: "numeric" }).format(new Date(ms));

// "EST", "PST", "GMT+5" — what a person expects to see next to a time.
export function tzAbbrev(ms, tz) {
  const part = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" }).formatToParts(new Date(ms)).find(p => p.type === "timeZoneName");
  return part ? part.value : tz;
}

export const todayKey = tz => dateKey(Date.now(), tz);

// Cells for a month grid: leading blanks so day 1 lands under its weekday,
// then one cell per day. Calendar dates are timezone-independent labels, so
// plain UTC arithmetic is exactly right here.
export function monthCells(year, month) {
  const lead = new Date(Date.UTC(year, month, 1)).getUTCDay();
  const days = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const cells = Array(lead).fill(null);
  for (let d = 1; d <= days; d++) cells.push({ day: d, key: `${year}-${two(month + 1)}-${two(d)}` });
  return cells;
}

export const monthLabel = (year, month) =>
  new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "long", year: "numeric" }).format(new Date(Date.UTC(year, month, 1)));

// Curated for the audience (Canada/US first) with the visitor's own zone
// always present so the picker never shows a blank.
const COMMON_ZONES = [
  "America/St_Johns", "America/Halifax", "America/Toronto", "America/Winnipeg", "America/Edmonton", "America/Vancouver",
  "America/New_York", "America/Chicago", "America/Denver", "America/Phoenix", "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu",
  "Europe/London", "Europe/Paris", "Asia/Dubai", "Asia/Karachi", "Asia/Kolkata", "Asia/Singapore", "Australia/Sydney", "UTC",
];
export function timezoneOptions(...extra) {
  const all = [...new Set([...extra.filter(Boolean), ...COMMON_ZONES])];
  return all.map(z => ({ value: z, label: z.replace(/_/g, " ") }));
}
